import {
  FC,
  Key,
  useRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import cn from 'classnames';
import moment from 'moment-timezone';
import deepEqual from 'fast-deep-equal';
import { Playlist, Frequency } from '@raydiant/api-client-js';
import Dialog, {
  DialogHeader,
  DialogHeading,
  DialogTrigger,
} from '@raydiant/raydial/components/Dialog';
import Modal from '@raydiant/raydial/components/Modal';
import Button from '@raydiant/raydial/components/Button';
import Text from '@raydiant/raydial/components/Text';
import Toggle from '@raydiant/raydial/components/Toggle';
import Select, { SelectItem } from '@raydiant/raydial/components/Select';
import { ArrowLeft } from '@raydiant/raydial/components/Icons';
import analytics from '../../../../../clients/analyticsClient';
import usePlaylistPageContext from '../../../usePlaylistPageContext';
import PlaylistSchedulePreviewModalV2 from '../PlaylistSchedulePreviewModalV2/PlaylistSchedulePreviewModalV2';
import stepScheduleIcon from '../../assets/step-schedule.svg';
import Popover from '@raydiant/raydial/components/Popover';
import InfoCircle from '../../../../LocationsPage/Icons/InfoCircle';
import { Edit } from '@raydiant/raydial/components/Icons';
import {
  RepeatOption,
  CustomFrequency,
  Weekday,
  WEEKDAYS,
  CUSTOM_FREQ_OPTIONS,
  getCurrentWeekday,
  customFreqToFrequency,
  getRepeatOptions,
  repeatOptionToRecurrenceRule,
  recurrenceRuleToRepeatOption,
  toDateInputValue,
  toTimeInputValue,
  combineDatetime,
  toMonthDayValue,
  toYearRepeatDateValue,
  parseYearRepeatParts,
  toYearDateFromRule,
  toLegacyRecurrenceRule,
  openNativePicker,
  localTimezone,
} from './scheduleUtils';

interface PlaylistScheduleModalV2Props {
  isOpen: boolean;
  playlist: Playlist | null;
  onClose: () => void;
}

const PlaylistScheduleModalV2: FC<PlaylistScheduleModalV2Props> = ({
  isOpen,
  playlist,
  onClose,
}) => {
  const { updatePlaylist } = usePlaylistPageContext();

  const startDateRef = useRef<HTMLInputElement>(null);
  const startTimeRef = useRef<HTMLInputElement>(null);
  const endDateRef = useRef<HTMLInputElement>(null);
  const endTimeRef = useRef<HTMLInputElement>(null);
  const customYearDateRef = useRef<HTMLInputElement>(null);

  const [enableScheduling, setEnableScheduling] = useState(false);
  const [scheduleType, setScheduleType] = useState<string>('join');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [isRepeating, setIsRepeating] = useState(false);
  const [recurrenceRule, setRecurrenceRule] =
    useState<Playlist['recurrenceRule']>(null);
  const [repeatOption, setRepeatOption] = useState<RepeatOption>('none');
  const [isAllDay, setIsAllDay] = useState(false);
  const [prevEndDate, setPrevEndDate] = useState('');
  const [prevEndTime, setPrevEndTime] = useState('');
  const [autoRemoveOnScheduleEnd, setAutoRemoveOnScheduleEnd] = useState(false);

  const [customInterval, setCustomInterval] = useState(1);
  const [customFreq, setCustomFreq] = useState<CustomFrequency>('week');
  const [customDays, setCustomDays] = useState<Weekday[]>([
    getCurrentWeekday(),
  ]);
  const [customMonthDay, setCustomMonthDay] = useState(1);
  const [customYearDate, setCustomYearDate] = useState(
    moment().format('YYYY-MM-DD'),
  );
  const [isCustomRepeatModalOpen, setIsCustomRepeatModalOpen] = useState(false);
  const [customDraftInterval, setCustomDraftInterval] = useState(1);
  const [customDraftFreq, setCustomDraftFreq] =
    useState<CustomFrequency>('week');
  const [customDraftDays, setCustomDraftDays] = useState<Weekday[]>([
    getCurrentWeekday(),
  ]);
  const [customDraftMonthDay, setCustomDraftMonthDay] = useState(1);
  const [customDraftYearDate, setCustomDraftYearDate] = useState(
    moment().format('YYYY-MM-DD'),
  );
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const handleCloseModal = useCallback(() => {
    setIsPreviewOpen(false);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!playlist) return;
    setEnableScheduling(!!playlist.startDatetime);
    setScheduleType(playlist.scheduleType);
    setStartDate(toDateInputValue(playlist.startDatetime));
    setStartTime(toTimeInputValue(playlist.startDatetime));
    setEndDate(toDateInputValue(playlist.endDatetime));
    setEndTime(toTimeInputValue(playlist.endDatetime));
    setIsRepeating(!!playlist.recurrenceRule);
    setRecurrenceRule(playlist.recurrenceRule);
    setRepeatOption(recurrenceRuleToRepeatOption(playlist.recurrenceRule));

    const endTime = toTimeInputValue(playlist.endDatetime);
    const endDate = toDateInputValue(playlist.endDatetime);
    const startDate = toDateInputValue(playlist.startDatetime);
    const expectedAllDayEndDate = moment(startDate)
      .add(1, 'day')
      .format('YYYY-MM-DD');
    const detectedAllDay =
      endTime === '00:00' && endDate === expectedAllDayEndDate;
    setIsAllDay(detectedAllDay);
    if (detectedAllDay) {
      setPrevEndDate(startDate);
      setPrevEndTime('23:59');
    }
    setAutoRemoveOnScheduleEnd(!!playlist.autoRemoveOnScheduleEnd);

    if (playlist.recurrenceRule) {
      const rule = playlist.recurrenceRule;
      const bymonth = (rule as unknown as { bymonth?: number[] }).bymonth;
      const isAnnuallyRule =
        rule.freq === Frequency.monthly &&
        (rule.interval || 1) === 12 &&
        (!bymonth || bymonth.length === 0);
      const isLegacyYearRule =
        rule.freq === Frequency.monthly &&
        (rule.interval || 1) % 12 === 0 &&
        !!bymonth?.length;

      setCustomInterval(
        isAnnuallyRule
          ? 1
          : isLegacyYearRule
          ? Math.max(1, (rule.interval || 12) / 12)
          : rule.interval || 1,
      );
      setCustomDays(
        rule.byday && rule.byday.length > 0
          ? ([...rule.byday] as Weekday[])
          : [getCurrentWeekday()],
      );
      const bymonthday = (rule as unknown as { bymonthday?: number[] })
        .bymonthday;
      setCustomMonthDay(
        bymonthday?.[0] ||
          toMonthDayValue(toDateInputValue(playlist.startDatetime)),
      );
      setCustomYearDate(
        toYearDateFromRule(
          rule,
          toYearRepeatDateValue(toDateInputValue(playlist.startDatetime)),
        ),
      );
      if (rule.freq === Frequency.weekly) setCustomFreq('week');
      else if (isAnnuallyRule || isLegacyYearRule) setCustomFreq('year');
      else if (rule.freq === Frequency.monthly) setCustomFreq('month');
    } else {
      setCustomInterval(1);
      setCustomFreq('week');
      setCustomDays([getCurrentWeekday()]);
      setCustomMonthDay(
        toMonthDayValue(toDateInputValue(playlist.startDatetime)),
      );
      setCustomYearDate(
        toYearRepeatDateValue(toDateInputValue(playlist.startDatetime)),
      );
    }
  }, [playlist]);

  const handleEnableSchedulingChange = useCallback(
    (enabled: boolean) => {
      if (playlist) {
        analytics.trackPlaylistScheduleClicked({
          playlistId: playlist.id,
          scheduleAction: 'enable_scheduling',
          scheduleValue: enabled,
        });
      }
      setEnableScheduling(enabled);

      if (enabled) {
        const now = moment();
        now.seconds(0);
        now.milliseconds(0);

        const todayEnd = moment(now).add(1, 'day').startOf('day');

        setScheduleType('join');
        setStartDate(now.format('YYYY-MM-DD'));
        setStartTime(now.format('HH:mm'));
        setEndDate(todayEnd.format('YYYY-MM-DD'));
        setEndTime(todayEnd.format('HH:mm'));
        setIsRepeating(false);
        setRecurrenceRule(null);
        setRepeatOption('none');
        setIsAllDay(false);
        setAutoRemoveOnScheduleEnd(true);
      } else {
        setIsRepeating(false);
        setRecurrenceRule(null);
        setRepeatOption('none');
        setIsAllDay(false);
        setStartDate('');
        setStartTime('');
        setEndDate('');
        setEndTime('');
        setAutoRemoveOnScheduleEnd(false);
      }
    },
    [playlist],
  );

  const handleRepeatChange = useCallback(
    (key: Key) => {
      const option = key as RepeatOption;
      if (playlist) {
        analytics.trackPlaylistScheduleClicked({
          playlistId: playlist.id,
          scheduleAction: 'repeat_option',
          scheduleValue: option,
        });
      }
      setRepeatOption(option);
      if (option === 'none') {
        setIsRepeating(false);
        setRecurrenceRule(null);
      } else if (option === 'custom') {
        setIsRepeating(true);
        const isAnnuallyContext =
          repeatOption === 'annually' ||
          recurrenceRuleToRepeatOption(recurrenceRule) === 'annually';
        const startBasedYearDate = toYearRepeatDateValue(startDate);
        if (isAnnuallyContext) {
          setCustomDraftInterval(1);
          setCustomDraftFreq('year');
          setCustomDraftDays([getCurrentWeekday()]);
          setCustomDraftMonthDay(toMonthDayValue(startDate));
          setCustomDraftYearDate(startBasedYearDate);
        } else {
          setCustomDraftInterval(customInterval);
          setCustomDraftFreq(customFreq);
          setCustomDraftDays(
            customDays.length > 0 ? customDays : [getCurrentWeekday()],
          );
          setCustomDraftMonthDay(customMonthDay);
          setCustomDraftYearDate(customYearDate);
        }
        setIsCustomRepeatModalOpen(true);
      } else {
        setIsRepeating(true);
        setAutoRemoveOnScheduleEnd(false);
        setRecurrenceRule(repeatOptionToRecurrenceRule(option));
      }
    },
    [
      playlist,
      customFreq,
      customInterval,
      customDays,
      customMonthDay,
      customYearDate,
      repeatOption,
      recurrenceRule,
      startDate,
    ],
  );

  const buildCustomRecurrenceRule = useCallback(() => {
    const normalizedMonthDay = Math.min(31, Math.max(1, customMonthDay || 1));
    const rule: Playlist['recurrenceRule'] = {
      dtstart: '',
      freq: customFreqToFrequency(customFreq),
      interval: customFreq === 'year' ? customInterval * 12 : customInterval,
    };
    if (customFreq === 'week') {
      rule!.byday = customDays.length > 0 ? customDays : [getCurrentWeekday()];
    }
    if (customFreq === 'month') {
      (rule as unknown as { bymonthday?: number[] }).bymonthday = [
        normalizedMonthDay,
      ];
    }
    if (customFreq === 'year') {
      const yearParts = parseYearRepeatParts(customYearDate);
      if (yearParts) {
        const yearRule = rule as unknown as {
          bymonth?: number[];
          bymonthday?: number[];
        };
        yearRule.bymonth = [yearParts.bymonth];
        yearRule.bymonthday = [yearParts.bymonthday];
      }
    }
    return rule;
  }, [customFreq, customInterval, customDays, customMonthDay, customYearDate]);

  useEffect(() => {
    if (repeatOption !== 'custom') return;
    // Keep draft-only edits isolated while the custom modal is open.
    // Commit to recurrenceRule only after closing (Done path).
    if (isCustomRepeatModalOpen) return;
    setRecurrenceRule(buildCustomRecurrenceRule());
  }, [repeatOption, isCustomRepeatModalOpen, buildCustomRecurrenceRule]);

  useEffect(() => {
    if (!isAllDay || !startDate) return;
    setEndDate(moment(startDate).add(1, 'day').format('YYYY-MM-DD'));
    setEndTime('00:00');
  }, [isAllDay, startDate]);

  useEffect(() => {
    if (!isCustomRepeatModalOpen) return;
    if (customDraftFreq !== 'week') return;
    if (customDraftDays.length > 0) return;
    setCustomDraftDays([getCurrentWeekday()]);
  }, [isCustomRepeatModalOpen, customDraftFreq, customDraftDays.length]);

  const toggleCustomDraftDay = useCallback((day: Weekday) => {
    setCustomDraftDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day],
    );
  }, []);

  const handleAllDayChange = useCallback(
    (checked: boolean) => {
      if (playlist) {
        analytics.trackPlaylistScheduleClicked({
          playlistId: playlist.id,
          scheduleAction: 'all_day',
          scheduleValue: checked,
        });
      }
      setIsAllDay(checked);
      if (checked) {
        if (!startDate) return;
        setPrevEndDate(endDate);
        setPrevEndTime(endTime);
        setEndDate(moment(startDate).add(1, 'day').format('YYYY-MM-DD'));
        setEndTime('00:00');
      } else {
        setEndDate(prevEndDate || endDate);
        setEndTime(prevEndTime || endTime);
      }
    },
    [playlist, startDate, endDate, endTime, prevEndDate, prevEndTime],
  );

  const handleScheduleTypeChange = useCallback(
    (type: string) => {
      if (playlist) {
        analytics.trackPlaylistScheduleClicked({
          playlistId: playlist.id,
          scheduleAction: 'schedule_type',
          scheduleValue: type,
        });
      }
      setScheduleType(type);
    },
    [playlist],
  );

  const handleAutoRemoveChange = useCallback(
    (checked: boolean) => {
      if (playlist) {
        analytics.trackPlaylistScheduleClicked({
          playlistId: playlist.id,
          scheduleAction: 'auto_remove',
          scheduleValue: checked,
        });
      }
      setAutoRemoveOnScheduleEnd(checked);
    },
    [playlist],
  );

  const openCustomRepeatModal = useCallback(() => {
    const isAnnuallyContext =
      repeatOption === 'annually' ||
      recurrenceRuleToRepeatOption(recurrenceRule) === 'annually';
    const startBasedYearDate = toYearRepeatDateValue(startDate);

    if (isAnnuallyContext) {
      setCustomDraftInterval(1);
      setCustomDraftFreq('year');
      setCustomDraftDays([getCurrentWeekday()]);
      setCustomDraftMonthDay(toMonthDayValue(startDate));
      setCustomDraftYearDate(startBasedYearDate);
    } else {
      setCustomDraftInterval(customInterval);
      setCustomDraftFreq(customFreq);
      setCustomDraftDays(
        customDays.length > 0 ? customDays : [getCurrentWeekday()],
      );
      setCustomDraftMonthDay(customMonthDay);
      setCustomDraftYearDate(customYearDate);
    }
    setIsCustomRepeatModalOpen(true);
  }, [
    customInterval,
    customFreq,
    customDays,
    customMonthDay,
    customYearDate,
    repeatOption,
    recurrenceRule,
    startDate,
  ]);

  const handleCustomRepeatCancel = useCallback(() => {
    setRepeatOption(recurrenceRuleToRepeatOption(recurrenceRule));
    setIsCustomRepeatModalOpen(false);
  }, [recurrenceRule]);

  const handleCustomRepeatDone = useCallback(() => {
    const normalizedMonthDay = Math.min(
      31,
      Math.max(1, customDraftMonthDay || 1),
    );
    const normalizedWeekDays =
      customDraftFreq === 'week' && customDraftDays.length === 0
        ? [getCurrentWeekday()]
        : customDraftDays;

    setAutoRemoveOnScheduleEnd(false);
    setCustomInterval(customDraftInterval);
    setCustomFreq(customDraftFreq);
    setCustomDays(normalizedWeekDays);
    setCustomMonthDay(normalizedMonthDay);
    setCustomYearDate(customDraftYearDate);

    setIsRepeating(true);
    setRepeatOption('custom');
    const yearParts = parseYearRepeatParts(customDraftYearDate);
    setRecurrenceRule({
      dtstart: '',
      freq: customFreqToFrequency(customDraftFreq),
      interval:
        customDraftFreq === 'year'
          ? customDraftInterval * 12
          : customDraftInterval,
      ...(customDraftFreq === 'week' ? { byday: normalizedWeekDays } : {}),
      ...(customDraftFreq === 'month'
        ? ({
            bymonthday: [normalizedMonthDay],
          } as unknown as Record<string, unknown>)
        : {}),
      ...(customDraftFreq === 'year' && yearParts
        ? ({
            bymonth: [yearParts.bymonth],
            bymonthday: [yearParts.bymonthday],
          } as unknown as Record<string, unknown>)
        : {}),
    });

    setIsCustomRepeatModalOpen(false);
  }, [
    customDraftInterval,
    customDraftFreq,
    customDraftDays,
    customDraftMonthDay,
    customDraftYearDate,
  ]);

  const isInvalidDateRange = useMemo(() => {
    if (!startDate || !endDate) return false;
    return (
      combineDatetime(startDate, startTime) > combineDatetime(endDate, endTime)
    );
  }, [startDate, startTime, endDate, endTime]);

  const isDirty = useMemo(() => {
    if (!playlist) return false;
    if (enableScheduling !== !!playlist.startDatetime) return true;
    if (scheduleType !== playlist.scheduleType) return true;
    if (autoRemoveOnScheduleEnd !== !!playlist.autoRemoveOnScheduleEnd)
      return true;
    if (startDate !== toDateInputValue(playlist.startDatetime)) return true;
    if (startTime !== toTimeInputValue(playlist.startDatetime)) return true;
    if (endDate !== toDateInputValue(playlist.endDatetime)) return true;
    if (endTime !== toTimeInputValue(playlist.endDatetime)) return true;
    if (isRepeating && !playlist.recurrenceRule) return true;
    if (!isRepeating && playlist.recurrenceRule) return true;

    if (recurrenceRule && playlist.recurrenceRule) {
      const prevRRule = playlist.recurrenceRule;
      if (recurrenceRule.freq !== prevRRule.freq) return true;
      if (recurrenceRule.interval !== prevRRule.interval) return true;
      if (!deepEqual(recurrenceRule.byday, prevRRule.byday)) return true;
      if (
        !deepEqual(
          (recurrenceRule as unknown as { bymonthday?: number[] }).bymonthday,
          (prevRRule as unknown as { bymonthday?: number[] }).bymonthday,
        )
      )
        return true;
      if (
        !deepEqual(
          (recurrenceRule as unknown as { bymonth?: number[] }).bymonth,
          (prevRRule as unknown as { bymonth?: number[] }).bymonth,
        )
      )
        return true;
    }

    return false;
  }, [
    playlist,
    enableScheduling,
    scheduleType,
    autoRemoveOnScheduleEnd,
    startDate,
    startTime,
    endDate,
    endTime,
    isRepeating,
    recurrenceRule,
  ]);

  const hasStartError = enableScheduling && (!startDate || !startTime);
  const hasEndError =
    enableScheduling && (!endDate || !endTime || isInvalidDateRange);

  const enableSave =
    isDirty && (!enableScheduling || (!hasStartError && !hasEndError));

  const handleSave = useCallback(() => {
    if (!playlist) return;

    const params: Partial<Playlist> = {
      startDatetime: null,
      endDatetime: null,
      tzid: null,
      recurrenceRule: null,
      scheduleType,
      autoRemoveOnScheduleEnd: enableScheduling
        ? autoRemoveOnScheduleEnd
        : false,
    };

    if (enableScheduling) {
      if (startDate) {
        params.startDatetime = combineDatetime(startDate, startTime);
      }

      if (endDate) {
        params.endDatetime = combineDatetime(endDate, endTime);
      }

      if (isRepeating && recurrenceRule) {
        const legacyRule = toLegacyRecurrenceRule({
          rule: recurrenceRule,
          repeatOption,
          customFreq,
          customInterval,
          startDate,
          customYearDate,
        });
        params.recurrenceRule = {
          ...(legacyRule || recurrenceRule),
          dtstart: params.startDatetime || '',
        };
      }

      params.tzid = localTimezone;
    }

    analytics.trackPlaylistScheduleSaved({ playlistId: playlist.id });
    updatePlaylist(playlist.id, params);
    onClose();
  }, [
    playlist,
    enableScheduling,
    autoRemoveOnScheduleEnd,
    scheduleType,
    startDate,
    startTime,
    endDate,
    endTime,
    isRepeating,
    recurrenceRule,
    repeatOption,
    customFreq,
    customInterval,
    customYearDate,
    updatePlaylist,
    onClose,
  ]);

  if (!playlist) return null;

  if (isPreviewOpen) {
    return (
      <Modal
        isOpen={isOpen}
        isDismissable
        onOpenChange={(open) => {
          if (!open) {
            setIsPreviewOpen(false);
            onClose();
          }
        }}
        className="w-[95vw] !max-w-[1200px] max-h-[85vh] overflow-hidden"
      >
        <Dialog
          showCloseButton
          onClose={() => setIsPreviewOpen(false)}
          className="flex flex-col overflow-hidden"
          style={{
            width: '95vw',
            maxWidth: '1200px',
            height: '85vh',
            maxHeight: '900px',
          }}
        >
          <div className="shrink-0">
            <DialogHeader>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsPreviewOpen(false)}
                  className="p-1"
                  aria-label="Back to schedule settings"
                >
                  <ArrowLeft size={20} className="text-black" />
                </button>
                <div>
                  <DialogHeading>
                    <span className="text-black">Schedule Preview</span>
                  </DialogHeading>
                  <Text className="text-gray-600 text-lg">{playlist.name}</Text>
                </div>
              </div>
            </DialogHeader>
          </div>
          <PlaylistSchedulePreviewModalV2
            playlistName={playlist.name}
            enableScheduling={enableScheduling}
            isRepeating={isRepeating}
            startDate={startDate}
            startTime={startTime}
            endDate={endDate}
            endTime={endTime}
            repeatOption={repeatOption}
            customFreq={customFreq}
            customInterval={customInterval}
            customDays={customDays}
            customMonthDay={customMonthDay}
            customYearDate={customYearDate}
          />
        </Dialog>
      </Modal>
    );
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        isDismissable
        onOpenChange={(open) => {
          if (!open) handleCloseModal();
        }}
      >
        <Dialog
          showCloseButton
          onClose={handleCloseModal}
          className="max-h-[90vh] flex flex-col overflow-hidden"
        >
          <div className="shrink-0">
            <DialogHeader>
              <DialogHeading>
                <span className="text-black">Schedule Playlist</span>
              </DialogHeading>
              <Text className="text-gray-600 text-lg">{playlist.name}</Text>
            </DialogHeader>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto hide-scrollbar">
            <div className="flex flex-col gap-8 px-4">
              {/* Scheduling enablement */}
              <div className="px-4 py-6 border-2 rounded-2xl border-gray-200">
                <Toggle
                  className="ml-4"
                  isSelected={enableScheduling}
                  onChange={handleEnableSchedulingChange}
                >
                  <Text variant="body" className="text-black">
                    Enable scheduling for this content
                  </Text>
                </Toggle>
              </div>

              {enableScheduling ? (
                <div className="flex flex-col gap-8">
                  {/* Schedule Details */}
                  <div>
                    <div className="flex items-center">
                      <Text variant="smallSubheader" className="text-black">
                        Schedule Type
                      </Text>

                      <DialogTrigger>
                        <Button aria-label="Help" variant="unstyled">
                          <div className="flex items-center ml-2">
                            <InfoCircle size={17} className="text-gray-700" />
                          </div>
                        </Button>
                        <Popover
                          className="max-w-[250px]"
                          placement="bottom start"
                        >
                          <Dialog>
                            <div className="overflow-hidden">
                              <p>
                                Choose to add this content to other content in
                                your parent playlist or override all content
                                with this playlist.
                              </p>
                            </div>
                          </Dialog>
                        </Popover>
                      </DialogTrigger>
                    </div>

                    <div
                      className={cn(
                        'flex py-1 px-1.5 bg-gray-100 rounded-xl gap-1 mt-4',
                        !enableScheduling && 'opacity-90 pointer-events-none',
                      )}
                    >
                      <Button
                        className={cn(
                          'flex-1 px-4 py-2 rounded-xl text-[#767578] text-lg font-medium',
                          scheduleType === 'join' && 'bg-blue-600 text-white',
                        )}
                        variant="unstyled"
                        onPress={() => handleScheduleTypeChange('join')}
                        isDisabled={!enableScheduling}
                      >
                        Add to Content
                      </Button>
                      <Button
                        className={cn(
                          'flex-1 px-4 py-2 rounded-xl text-[#767578] text-lg font-medium',
                          scheduleType === 'override' &&
                            'bg-blue-600 text-white',
                        )}
                        variant="unstyled"
                        onPress={() => handleScheduleTypeChange('override')}
                        isDisabled={!enableScheduling}
                      >
                        Override Content
                      </Button>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between">
                      <div className="flex items-center">
                        <Text variant="smallSubheader" className="text-black">
                          Schedule Details
                        </Text>

                        <DialogTrigger>
                          <Button aria-label="Help" variant="unstyled">
                            <div className="flex items-center ml-2">
                              <InfoCircle size={17} className="text-gray-700" />
                            </div>
                          </Button>
                          <Popover
                            className="max-w-[250px]"
                            placement="bottom start"
                          >
                            <Dialog>
                              <div className="overflow-hidden">
                                <p>
                                  If no repetition is set, this playlist plays
                                  from your start time to your end time across
                                  the selected date range. Excluded days won’t
                                  be played going forward. If today is excluded,
                                  it will still play today but skip from the
                                  next occurrence.
                                </p>
                              </div>
                            </Dialog>
                          </Popover>
                        </DialogTrigger>
                      </div>

                      <div className="flex items-center pl-4 mt-4">
                        <input
                          id="allday-checkbox"
                          type="checkbox"
                          checked={isAllDay}
                          onChange={(e) => handleAllDayChange(e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        />
                        <label
                          htmlFor="allday-checkbox"
                          className="text-lg text-gray-700 cursor-pointer"
                        >
                          All Day
                        </label>
                      </div>
                    </div>

                    {/* Datetime range */}
                    <div className="flex flex-col mt-4">
                      <div className="flex flex-col md:flex-row md:items-center gap-2">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <div className="relative flex flex-1">
                            <button
                              type="button"
                              onClick={() =>
                                openNativePicker(startDateRef.current)
                              }
                              aria-label="Select start date"
                              className={cn(
                                'w-full flex justify-center items-center h-12 bg-gray-100 rounded-2xl px-4 p-4 text-base text-gray-700 cursor-pointer whitespace-nowrap outline-none',
                                hasStartError
                                  ? 'ring-2 ring-inset ring-red-600'
                                  : 'focus:ring-2 focus:ring-inset focus:ring-blue-600',
                              )}
                            >
                              {startDate
                                ? moment(startDate).format('ddd, MMM D, YYYY')
                                : 'Select date'}
                            </button>
                            <input
                              ref={startDateRef}
                              type="date"
                              value={startDate}
                              onChange={(e) => setStartDate(e.target.value)}
                              onFocus={(e) => e.preventDefault()}
                              className="absolute inset-0 opacity-0 pointer-events-none"
                              tabIndex={-1}
                            />
                          </div>
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() =>
                                openNativePicker(startTimeRef.current)
                              }
                              aria-label="Select start time"
                              className={cn(
                                'w-full flex justify-center items-center h-12 bg-gray-100 rounded-2xl px-4 p-4 text-base text-gray-700 whitespace-nowrap md:max-w-[110px] outline-none cursor-pointer',
                                hasStartError
                                  ? 'ring-2 ring-inset ring-red-600'
                                  : 'focus:ring-2 focus:ring-inset focus:ring-blue-600',
                              )}
                            >
                              {startTime
                                ? moment(startTime, 'HH:mm').format('hh:mm A')
                                : 'Select time'}
                            </button>
                            <input
                              ref={startTimeRef}
                              type="time"
                              value={startTime}
                              onChange={(e) => setStartTime(e.target.value)}
                              onFocus={(e) => e.preventDefault()}
                              className="absolute inset-0 opacity-0 pointer-events-none"
                              tabIndex={-1}
                            />
                          </div>
                        </div>

                        <div className="text-center">
                          <Text variant="smallSubheader" className="text-black">
                            to
                          </Text>
                        </div>

                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <div className="relative flex flex-1">
                            <button
                              type="button"
                              onClick={() => {
                                if (!isAllDay)
                                  openNativePicker(endDateRef.current);
                              }}
                              aria-label="Select end date"
                              disabled={isAllDay}
                              aria-disabled={isAllDay}
                              tabIndex={isAllDay ? -1 : 0}
                              className={cn(
                                'w-full flex justify-center items-center h-12 bg-gray-100 rounded-2xl px-4 p-4 text-base text-gray-700 whitespace-nowrap outline-none',
                                isAllDay
                                  ? 'opacity-50 cursor-default'
                                  : 'cursor-pointer',
                                hasEndError
                                  ? 'ring-2 ring-inset ring-red-600'
                                  : 'focus:ring-2 focus:ring-inset focus:ring-blue-600',
                              )}
                            >
                              {endDate
                                ? moment(endDate).format('ddd, MMM D, YYYY')
                                : 'Select date'}
                            </button>
                            <input
                              ref={endDateRef}
                              type="date"
                              value={endDate}
                              onChange={(e) => setEndDate(e.target.value)}
                              disabled={isAllDay}
                              onFocus={(e) => e.preventDefault()}
                              className="absolute inset-0 opacity-0 pointer-events-none"
                              tabIndex={-1}
                            />
                          </div>
                          <div
                            className={cn(
                              'w-full md:w-[110px] md:max-w-[110px]',
                            )}
                          >
                            <button
                              type="button"
                              onClick={() => {
                                if (!isAllDay) {
                                  openNativePicker(endTimeRef.current);
                                }
                              }}
                              aria-label="Select end time"
                              disabled={isAllDay}
                              aria-disabled={isAllDay}
                              tabIndex={isAllDay ? -1 : 0}
                              className={cn(
                                'w-full flex justify-center items-center h-12 bg-gray-100 rounded-2xl px-4 p-4 text-base text-gray-700 whitespace-nowrap outline-none',
                                isAllDay
                                  ? 'opacity-50 cursor-default'
                                  : 'cursor-pointer',
                                hasEndError && !isAllDay
                                  ? 'ring-2 ring-inset ring-red-600'
                                  : 'focus:ring-2 focus:ring-inset focus:ring-blue-600',
                              )}
                            >
                              {endTime
                                ? moment(endTime, 'HH:mm').format('hh:mm A')
                                : 'Select time'}
                            </button>
                            <input
                              ref={endTimeRef}
                              type="time"
                              value={endTime}
                              onChange={(e) => setEndTime(e.target.value)}
                              disabled={isAllDay}
                              onFocus={(e) => e.preventDefault()}
                              className="absolute inset-0 opacity-0 pointer-events-none"
                              tabIndex={-1}
                            />
                          </div>
                        </div>
                      </div>

                      {isInvalidDateRange && (
                        <Text
                          variant="body"
                          className="text-red-600 text-xs ml-4 mt-1"
                        >
                          End time must be later than start time
                        </Text>
                      )}
                    </div>
                  </div>

                  {/* Recurrence configuration */}
                  <div className="flex flex-col gap-2">
                    <Text
                      variant="body"
                      className="ml-3 text-base text-gray-600"
                    >
                      Repetition Options
                    </Text>
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <Select
                          selectedKey={repeatOption}
                          onSelectionChange={handleRepeatChange}
                          placeholder="Does not repeat"
                        >
                          {getRepeatOptions(startDate).map((opt) => (
                            <SelectItem key={opt.key} id={opt.key}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </Select>
                      </div>
                      {repeatOption === 'custom' && (
                        <Button
                          variant="unstyled"
                          aria-label="Edit custom repetition options"
                          onPress={openCustomRepeatModal}
                          className="w-[30px] h-[30px] rounded-xl flex items-center justify-center text-acctconsole hover:text-blue-600"
                        >
                          <Edit size={18} />
                        </Button>
                      )}
                    </div>

                    <div className="flex items-center justify-between gap-4">
                      <button
                        type="button"
                        className="text-base text-blue-600 hover:underline ml-[15px]"
                        onClick={() => setIsPreviewOpen(true)}
                      >
                        Schedule Preview
                      </button>
                    </div>
                  </div>

                  {/* Completed Schedule Options */}
                  {repeatOption === 'none' && (
                    <div>
                      <div className="flex items-center mb-4">
                        <Text variant="smallSubheader" className="text-black">
                          Completed Schedule Options
                        </Text>

                        <DialogTrigger>
                          <Button aria-label="Help" variant="unstyled">
                            <div className="flex items-center ml-2">
                              <InfoCircle size={17} className="text-gray-700" />
                            </div>
                          </Button>
                          <Popover
                            className="max-w-[250px]"
                            placement="bottom start"
                          >
                            <Dialog>
                              <div className="overflow-hidden">
                                <p>
                                  If this is a nested playlist, it will be
                                  removed from the parent playlist after the
                                  schedule on this playlist is completed.
                                </p>
                              </div>
                            </Dialog>
                          </Popover>
                        </DialogTrigger>
                      </div>

                      <label className="flex items-center gap-3 ml-4 my-2 select-none cursor-pointer">
                        <input
                          id="remove-on-complete-checkbox"
                          type="checkbox"
                          checked={autoRemoveOnScheduleEnd}
                          onChange={(e) =>
                            handleAutoRemoveChange(e.target.checked)
                          }
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        />
                        <span className="text-gray-700 text-lg">
                          Remove content from playlist after schedule is
                          complete
                        </span>
                      </label>
                    </div>
                  )}
                </div>
              ) : (
                <div className="px-6 py-4 min-h-[460px] md:min-h-[444px] flex items-center justify-center">
                  <div className="max-w-[400px] flex flex-col items-center text-center">
                    <img src={stepScheduleIcon} alt="" className="w-20 h-22" />
                    <Text
                      variant="subheader"
                      as="h3"
                      className="text-black my-4"
                    >
                      Set a schedule
                    </Text>
                    <Text variant="body" as="p" className="mb-6">
                      Schedules help you display content at specific dates and
                      times for reoccurring events, different menus, LTO&apos;s
                      and more!
                    </Text>
                    <Text variant="caption" as="p">
                      Need help? Check out our{' '}
                      <a
                        href="https://support.displai.ai/s/article/What-are-Nested-Playlists-1660092389785#enhanced-scheduling"
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        help articles
                      </a>
                    </Text>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="shrink-0 flex flex-col-reverse justify-between pt-6 pb-4 gap-3 px-0 md:gap-6 md:flex-row md:px-4">
            <Button
              onPress={onClose}
              variant="secondary"
              size="large"
              className="grow"
            >
              Cancel
            </Button>
            <Button
              onPress={handleSave}
              variant="primary"
              size="large"
              className="grow"
              isDisabled={!enableSave}
            >
              Save
            </Button>
          </div>
        </Dialog>
      </Modal>

      <Modal isOpen={isCustomRepeatModalOpen} className="!max-w-[600px]">
        <Dialog showCloseButton onClose={handleCustomRepeatCancel}>
          <DialogHeader>
            <DialogHeading>
              <span className="text-black">Custom Repetition Options</span>
            </DialogHeading>
            <Text className="text-gray-600 text-lg">{playlist.name}</Text>
          </DialogHeader>

          <div className="px-4 pt-4 flex flex-col gap-4">
            <div className="flex items-center">
              <Text
                variant="body"
                className="text-black font-medium text-lg whitespace-nowrap mr-4"
              >
                Repeat every
              </Text>

              <div className="flex gap-2 w-full">
                <input
                  type="number"
                  min={1}
                  max={99}
                  value={customDraftInterval}
                  onChange={(e) =>
                    setCustomDraftInterval(
                      Math.max(1, parseInt(e.target.value, 10) || 1),
                    )
                  }
                  className="w-24 h-12 bg-gray-100 rounded-2xl px-4 text-base text-gray-700 text-center outline-none focus:ring-2 focus:ring-blue-600"
                />
                <Select
                  className="w-full [&>button]:min-w-0"
                  selectedKey={customDraftFreq}
                  onSelectionChange={(key) =>
                    setCustomDraftFreq(key as CustomFrequency)
                  }
                >
                  {CUSTOM_FREQ_OPTIONS.map((opt) => (
                    <SelectItem key={opt.key} id={opt.key}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </Select>
              </div>
            </div>

            {customDraftFreq === 'week' && (
              <div className="flex flex-col gap-4">
                <Text variant="body" className="text-black text-base">
                  Repeat on
                </Text>
                <div className="flex gap-2">
                  {WEEKDAYS.map((day) => (
                    <button
                      key={day.key}
                      type="button"
                      onClick={() => toggleCustomDraftDay(day.key)}
                      aria-label={day.ariaLabel}
                      aria-pressed={customDraftDays.includes(day.key)}
                      className={cn(
                        'flex-1 h-10 md:h-12 rounded-2xl text-base md:text-lg font-semibold outline-none border-2 border-[#EAEAEA] text-black hover:border-blue-600',
                        customDraftDays.includes(day.key) &&
                          'bg-blue-600 text-white border-blue-600',
                      )}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {customDraftFreq === 'month' && (
              <div className="flex items-center">
                <Text
                  variant="body"
                  className="text-black text-base whitespace-nowrap mr-4"
                >
                  Repeat monthly on day number
                </Text>
                <input
                  type="number"
                  min={1}
                  max={31}
                  value={customDraftMonthDay}
                  onChange={(e) =>
                    setCustomDraftMonthDay(
                      Math.min(
                        31,
                        Math.max(1, parseInt(e.target.value, 10) || 1),
                      ),
                    )
                  }
                  className="w-24 h-12 bg-gray-100 rounded-2xl px-4 text-base text-gray-700 text-center outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>
            )}

            {customDraftFreq === 'year' && (
              <div className="flex flex-col gap-4">
                <Text
                  variant="body"
                  className="text-black text-base whitespace-nowrap"
                >
                  Repeat on
                </Text>
                <div className="relative flex-1">
                  <button
                    type="button"
                    onClick={() => openNativePicker(customYearDateRef.current)}
                    aria-label="Select yearly repeat date"
                    className="w-full flex items-center h-12 bg-gray-100 rounded-2xl px-4 text-base text-gray-700 outline-none focus:ring-2 focus:ring-blue-600"
                  >
                    {customDraftYearDate
                      ? moment(customDraftYearDate).format('MMMM D')
                      : 'Select date'}
                  </button>
                  <input
                    ref={customYearDateRef}
                    type="date"
                    value={customDraftYearDate}
                    onChange={(e) => setCustomDraftYearDate(e.target.value)}
                    onFocus={(e) => e.preventDefault()}
                    className="absolute inset-0 opacity-0 pointer-events-none"
                    tabIndex={-1}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col-reverse justify-between mt-8 mb-4 gap-3 px-0 md:gap-6 md:flex-row md:px-4">
            <Button
              onPress={handleCustomRepeatCancel}
              variant="secondary"
              size="large"
              className="grow"
            >
              Cancel
            </Button>
            <Button
              onPress={handleCustomRepeatDone}
              variant="primary"
              size="large"
              className="grow"
            >
              Done
            </Button>
          </div>
        </Dialog>
      </Modal>
    </>
  );
};

export default PlaylistScheduleModalV2;
